import { PublicClientApplication } from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import { msalConfig } from "./authConfig";
import { debug } from "@/utils/debug";
import { setEntraLoginHint } from "./login-hint";

export const msalInstance = new PublicClientApplication(msalConfig);

const ACTIVE_ACCOUNT_KEY = "fido.auth.activeAccountId";

export const persistActiveAccountId = (homeAccountId: string) => {
  try {
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, homeAccountId);
  } catch {
    /* storage may be unavailable */
  }
};

const getPersistedActiveAccountId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_ACCOUNT_KEY);
  } catch {
    return null;
  }
};

const logMsalCacheHealth = () => {
  try {
    const clientId = msalConfig.auth.clientId;
    const allKeys = Object.keys(localStorage);
    const msalKeys = allKeys.filter(
      (k) => k.includes(clientId) || k.startsWith("msal.")
    );

    const hasAccessToken = msalKeys.some((k) => k.includes("accesstoken"));
    const hasRefreshToken = msalKeys.some((k) => k.includes("refreshtoken"));
    const hasAccount = msalKeys.some((k) => k.includes("account"));
    const hasIdToken = msalKeys.some((k) => k.includes("idtoken"));

    const summary = {
      totalMsalKeys: msalKeys.length,
      hasAccessToken,
      hasRefreshToken,
      hasIdToken,
      hasAccount
    };

    debug.log("[auth] MSAL localStorage cache health on startup:", summary);
    if (!import.meta.env.DEV) {
      Sentry.addBreadcrumb({
        category: "msal",
        message: "MSAL cache health on startup",
        level: "info",
        data: summary
      });
    }
  } catch {
    debug.warn("[auth] Failed to inspect MSAL localStorage cache");
  }
};

export const initializeMsal = async (): Promise<PublicClientApplication> => {
  const isInIframe = window.self !== window.top;

  debug.log("[auth] initializeMsal start", {
    isInIframe,
    url: window.location.href
  });

  if (isInIframe) {
    debug.warn(
      "[auth] App loaded inside iframe — this may cause block_iframe_reload errors"
    );
    if (!import.meta.env.DEV) {
      Sentry.addBreadcrumb({
        category: "msal",
        message: "App loaded inside iframe — potential block_iframe_reload",
        level: "warning"
      });
    }
  }

  logMsalCacheHealth();

  await msalInstance.initialize();

  try {
    const redirectResult = await msalInstance.handleRedirectPromise();

    debug.log("[auth] handleRedirectPromise result:", {
      hasResult: !!redirectResult,
      account: redirectResult?.account?.username ?? null,
      tokenType: redirectResult?.tokenType ?? null,
      fromCache: redirectResult?.fromCache ?? null
    });

    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);
      persistActiveAccountId(redirectResult.account.homeAccountId);
      setEntraLoginHint(redirectResult.account.username);
    }
  } catch (error) {
    const errorCode = (error as { errorCode?: string })?.errorCode;
    const errorMessage = (error as { errorMessage?: string })?.errorMessage;
    const subError = (error as { subError?: string })?.subError;

    debug.error("[auth] handleRedirectPromise failed:", {
      errorCode,
      errorMessage,
      subError,
      isInIframe
    });

    if (!import.meta.env.DEV) {
      Sentry.captureException(error, {
        tags: { authPhase: "handleRedirectPromise", isInIframe },
        extra: { errorCode, subError }
      });
    }
  }

  const allAccounts = msalInstance.getAllAccounts();

  if (!msalInstance.getActiveAccount() && allAccounts.length > 0) {
    const persistedId = getPersistedActiveAccountId();
    const preferred = persistedId
      ? allAccounts.find((a) => a.homeAccountId === persistedId)
      : undefined;
    msalInstance.setActiveAccount(preferred ?? allAccounts[0]);
  }

  debug.log("[auth] initializeMsal complete", {
    hasActiveAccount: !!msalInstance.getActiveAccount(),
    totalAccounts: allAccounts.length,
    isInIframe
  });

  return msalInstance;
};
